import { Table, Button, Radio } from "antd";
import { mainColumns, nestedColumns } from "./MoneyTransactionsTableColumns";
import RefundModal from './RefundModal';
import CancelRefundModal from "./CancelRefundModal";
import { useState, useMemo, useEffect } from "react";
import LoaderComponent from "../../Loader";
import {PAYMENT_PRODUCT_TYPE} from "../../../constants/AppConstants";
import { useParams } from 'react-router';
import {isSupportLead, isSupportAgent3} from "../../../helpers/genericHelper";
import API from '../../../services/API';
import { message } from 'antd';
import useMoneyMovements from "./MoneyTransactions/useMoneyMovements";
import usePageState from "../../../helpers/usePageState";

function MoneyTransactions({ merchantList = [], loading, refreshList, charges, disputes }) {
    
    const [localMerchantList, setLocalMerchantList] = useState(merchantList);
    const [localLoading, setLocalLoading] = useState(false);
    const [dataFetched, setDataFetched] = useState(false);
    const [refundModalVisible, setRefundModalVisible] = useState(false);
    const [generalRefundModalVisible, setGeneralRefundModalVisible] = useState(false);
    const [currentData, setCurrentData] = useState(null);
    const [cancelRefundModalVisible, setCancelRefundModalVisible] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState({});
    const [productTypeFilter, setProductTypeFilter] = usePageState('money-product-filter', 'ALL');
    const [expandedRowKeys, setExpandedRowKeys] = usePageState('money-rows', ['0']);

    const mappedList = (dataFetched ? localMerchantList : merchantList)?.map((merchant) => {
        if (merchant.paymentMode === 'STRIPE_CARD') {
            return {
                ...merchant,
                key: merchant.transactionId,
                status: disputes[merchant.transactionId] && disputes[merchant.transactionId].length > 0 ? 'Disputed' : merchant.status,
            }
        }
        
        return { ...merchant, key: merchant.transactionId };
    });
    
    let { userId } = useParams();

    const { moneyMovements, loading: moneyMovementsLoading, getMoneyMovements } = useMoneyMovements({ userId });

    useEffect(() => {
        if (!dataFetched && (merchantList.length === 0 || !merchantList)) {
            fetchMoneyTransactionsData();
        }
    }, [userId, merchantList.length, dataFetched]);

    const fetchMoneyTransactionsData = async () => {
        try {
            setLocalLoading(true);
            const response = await API.getMoneyTransactions(userId);
            setLocalMerchantList(response || []);
            setDataFetched(true);
        } catch (error) {
            console.error('Error fetching money transactions data:', error);
            message.error('Failed to load money transactions data');
        } finally {
            setLocalLoading(false);
        }
    };

    const expandedRowRender = (row) => {
        return (
            <Table columns={nestedColumns} dataSource={[row]} pagination={false} />
        );
    };

    const showRefundModalVisible = (data) => {
        setCurrentData(data);
        setRefundModalVisible(true);
    }
    
    const showGeneralRefundModal = () => {
        setGeneralRefundModalVisible(true);
    }

    const closeRefundModal = () => {
        setRefundModalVisible(false);
    }
    
    const closeGeneralRefundModal = () => {
        setGeneralRefundModalVisible(false);
    }

    const showCancelRefundModalVisible = (record) => {
        setCancelRefundModalVisible(true);
        setSelectedTransaction({ transactionId: record.transactionId, userId: record.userId });
    };
    const filteredMerchantList = useMemo(
        () => productTypeFilter === 'ALL'
            ? mappedList
            : mappedList.filter(merchant => merchant.productType === productTypeFilter),
        [mappedList, productTypeFilter]
    );

    return (
        <>
            {(loading || localLoading) ? (
                <LoaderComponent />
            ) : (
                <>
                    {isSupportAgent3() && (
                        <Button
                        title="Refund to user from ava wallet"
                            type="danger"
                            onClick={() => showGeneralRefundModal()}>
                            {'Refund To User'}
                        </Button>
                    )}
                    <div style={{ marginBottom: '16px', marginTop: '16px' }}>
                        <Radio.Group
                            value={productTypeFilter}
                            onChange={(e) => setProductTypeFilter(e.target.value)}
                            buttonStyle='solid'
                        >
                            <Radio.Button value="ALL">All Products</Radio.Button>
                            <Radio.Button value={PAYMENT_PRODUCT_TYPE.CREDIT_CARD}>Credit Card</Radio.Button>
                            <Radio.Button value={PAYMENT_PRODUCT_TYPE.SAVINGS_BUILDER}>Savings Builder</Radio.Button>
                            <Radio.Button value={PAYMENT_PRODUCT_TYPE.SUBSCRIPTION}>Subscription</Radio.Button>
                            <Radio.Button value={PAYMENT_PRODUCT_TYPE.GENERAL_REFUND}>General Refund</Radio.Button>
                        </Radio.Group>
                    </div>
                    <Table
                        columns={[
                            ...mainColumns,
                            {
                                title: "Actions",
                                key: "actions",
                                render: (text, record) => (
                                    <>
                                        {isSupportLead() && record.productType === PAYMENT_PRODUCT_TYPE.SUBSCRIPTION && (
                                            <Button
                                                type="primary"
                                                onClick={() => showRefundModalVisible(record)}>
                                                {'Refund'}
                                            </Button>
                                        )}
                                        {isSupportLead() && record.status === "IN_PROGRESS" && record.silaTransactionId != null && (
                                            <Button
                                                type="danger"
                                                onClick={() => showCancelRefundModalVisible(record)}>
                                                {'Cancel Refund'}
                                            </Button>
                                        )}
                                    </>
                                )
                            }
                        ]}
                        expandable={{
                            expandedRowRender,
                            expandedRowKeys,
                            onExpandedRowsChange: setExpandedRowKeys,
                        }}
                        dataSource={filteredMerchantList}
                        pagination={false}
                        loading={loading || localLoading}
                    />
                </>
            )}
            <RefundModal
                refundModalVisible={refundModalVisible}
                closeRefundModal={closeRefundModal}
                data={currentData}
                userId={userId}
                refetchUserData={refreshList}
                stripeChargeId={currentData?.stripeChargeId}
                paymentProductType={PAYMENT_PRODUCT_TYPE.SUBSCRIPTION}
            />
            <RefundModal
                refundModalVisible={generalRefundModalVisible}
                closeRefundModal={closeGeneralRefundModal}
                userId={userId}
                refetchUserData={refreshList}
                paymentProductType={PAYMENT_PRODUCT_TYPE.GENERAL_REFUND}
            />
            <CancelRefundModal
                visible={cancelRefundModalVisible}
                onCancel={() => setCancelRefundModalVisible(false)}
                transactionId={selectedTransaction.transactionId}
                userId={selectedTransaction.userId}
                refetchUserData={refreshList}
            />
        </>
    );
}

export default MoneyTransactions;